import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  QrCode,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  FolderOpen,
  Layers,
  Cpu,
  Sparkles,
} from 'lucide-react'
import { getQrInfo, bindQrCode } from '../api/qr'
import { getProjects } from '../api/projects'
import { getElectricalPanels } from '../api/electricalPanels'
import { getProjectDevices } from '../api/deviceInstances'

export default function QrResolverPage() {
  const { code: rawCode } = useParams<{ code: string }>()
  const code = (rawCode || '').trim().toUpperCase()
  const navigate = useNavigate()

  // Selected values for binding
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null)
  const [selectedPanelId, setSelectedPanelId] = useState<number | null>(null)
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null)
  const [serialNumber, setSerialNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [binding, setBinding] = useState(false)
  const [bindError, setBindError] = useState<string | null>(null)
  const [deviceSearch, setDeviceSearch] = useState('')

  // 1. Fetch QR Code info
  const {
    data: qrData,
    isLoading: qrLoading,
    error: qrError,
    refetch: refetchQr,
  } = useQuery({
    queryKey: ['qr-info', code],
    queryFn: () => getQrInfo(code),
    enabled: !!code,
    retry: 1,
  })

  // 2. Fetch Projects (for binding wizard)
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    enabled: !!qrData && (!qrData.found || !qrData.is_bound),
  })

  // 3. Fetch Panels when project is selected
  const { data: panels = [] } = useQuery({
    queryKey: ['electricalPanels', selectedProjectId],
    queryFn: () => getElectricalPanels(selectedProjectId!),
    enabled: !!selectedProjectId,
  })

  // 4. Fetch Devices when panel is selected
  const { data: devices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ['projectDevices', selectedProjectId, selectedPanelId],
    queryFn: () => getProjectDevices(selectedProjectId!, selectedPanelId!),
    enabled: !!selectedProjectId && !!selectedPanelId,
  })

  // Auto-redirect if bound
  useEffect(() => {
    if (qrData?.found && qrData.is_bound && qrData.instance) {
      const targetUrl = `/projects/${qrData.instance.project_id}/panels/${qrData.instance.electrical_panel_id}/device-instances/${qrData.instance.id}`
      const timer = setTimeout(() => {
        navigate(targetUrl, { replace: true })
      }, 700)
      return () => clearTimeout(timer)
    }
  }, [qrData, navigate])

  const handleBindSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedInstanceId) {
      setBindError('Выберите устройство для привязки')
      return
    }

    setBinding(true)
    setBindError(null)
    try {
      await bindQrCode({
        code,
        device_instance_id: selectedInstanceId,
        serial_number: serialNumber.trim() || undefined,
        notes: notes.trim() || undefined,
      })

      // Redirect immediately to the bound device instance card
      navigate(
        `/projects/${selectedProjectId}/panels/${selectedPanelId}/device-instances/${selectedInstanceId}`,
        { replace: true }
      )
    } catch (err: any) {
      console.error('Bind error:', err)
      setBindError(err?.response?.data?.error || err?.message || 'Ошибка привязки стикера')
    } finally {
      setBinding(false)
    }
  }

  // Filtered devices list for quick search
  const filteredDevices = devices.filter((d) => {
    if (!deviceSearch) return true
    const term = deviceSearch.toLowerCase()
    return (
      (d.position_label && d.position_label.toLowerCase().includes(term)) ||
      (d.spec_name && d.spec_name.toLowerCase().includes(term)) ||
      (d.description && d.description.toLowerCase().includes(term)) ||
      (d.model && d.model.toLowerCase().includes(term))
    )
  })

  if (qrLoading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-blue-50 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Поиск устройства...</h2>
          <p className="text-sm text-gray-500 font-mono">#{code}</p>
        </div>
      </div>
    )
  }

  if (qrError) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Ошибка обработки QR-кода</h2>
          <p className="text-sm text-gray-500">Не удалось связаться с сервером.</p>
          <button
            onClick={() => refetchQr()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    )
  }

  // Case 1: Bound device found -> Show info card while auto-redirecting
  if (qrData?.found && qrData.is_bound && qrData.instance) {
    const inst = qrData.instance
    const targetUrl = `/projects/${inst.project_id}/panels/${inst.electrical_panel_id}/device-instances/${inst.id}`

    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center space-y-5 border border-green-100">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-50 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>

          <div>
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-gray-100 text-gray-700 mb-2">
              #{code}
            </span>
            <h2 className="text-2xl font-bold text-gray-900">
              {inst.position_label ? `Позиция ${inst.position_label}` : 'Устройство найдено'}
            </h2>
            <div className="text-sm font-medium text-blue-600 mt-1">
              {inst.panel_name || 'Щит'} • {inst.project_name || 'Проект'}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-xl text-left text-sm space-y-2 border border-gray-100">
            <div>
              <span className="text-gray-500 text-xs block">Тип / Номенклатура:</span>
              <span className="font-semibold text-gray-900">{inst.spec_name}</span>
            </div>
            {inst.spec_model && (
              <div>
                <span className="text-gray-500 text-xs block">Модель:</span>
                <span className="text-gray-800">{inst.spec_model} {inst.spec_manufacturer ? `(${inst.spec_manufacturer})` : ''}</span>
              </div>
            )}
            {inst.description && (
              <div>
                <span className="text-gray-500 text-xs block">Назначение:</span>
                <span className="text-gray-700">{inst.description}</span>
              </div>
            )}
          </div>

          <div className="pt-2">
            <Link
              to={targetUrl}
              replace
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium shadow-md transition-colors"
            >
              <span>Открыть карточку устройства</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Case 2: Code is NOT bound or NOT found yet -> Wizard for fast mobile binding
  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        {/* Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center">
                <QrCode className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Стикер #{code}</h1>
                <p className="text-blue-100 text-xs mt-0.5">
                  {qrData?.found ? 'QR-код зарегистрирован, но не привязан к устройству' : 'Новый QR-код (еще не в базе)'}
                </p>
              </div>
            </div>
            <div className="bg-white/20 px-3 py-1.5 rounded-lg text-xs font-mono font-semibold">
              E2 • {code}
            </div>
          </div>
        </div>

        {/* Wizard Form */}
        <form onSubmit={handleBindSubmit} className="p-6 space-y-6">
          <div className="text-sm text-gray-600">
            Закрепите этот QR-стикер за устройством в щите. После сохранения стикер будет мгновенно открывать эту карточку.
          </div>

          {bindError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {bindError}
            </div>
          )}

          {/* Step 1: Project */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              1. Выберите проект
            </label>
            <select
              value={selectedProjectId || ''}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null
                setSelectedProjectId(val)
                setSelectedPanelId(null)
                setSelectedInstanceId(null)
              }}
              required
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">-- Выберите проект --</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (#{p.id})
                </option>
              ))}
            </select>
          </div>

          {/* Step 2: Panel */}
          {selectedProjectId && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-indigo-600" />
                2. Выберите электрический щит
              </label>
              <select
                value={selectedPanelId || ''}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : null
                  setSelectedPanelId(val)
                  setSelectedInstanceId(null)
                }}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">-- Выберите щит --</option>
                {panels.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.location ? `(${p.location})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Step 3: Device selector */}
          {selectedPanelId && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                <Cpu className="w-4 h-4 text-green-600" />
                3. Выберите устройство в щите
              </label>

              {/* Quick filter input */}
              <input
                type="text"
                placeholder="Поиск устройства по обозначению (QF1, Реле...) или модели..."
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
                className="w-full mb-2 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
              />

              <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {devicesLoading ? (
                  <div className="p-4 text-center text-sm text-gray-500">Загрузка устройств...</div>
                ) : filteredDevices.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">Устройства не найдены</div>
                ) : (
                  filteredDevices.map((d) => {
                    const isSelected = selectedInstanceId === d.id
                    return (
                      <div
                        key={d.id}
                        onClick={() => setSelectedInstanceId(d.id)}
                        className={`p-3 cursor-pointer flex items-center justify-between text-sm transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="min-w-0 pr-2">
                          <div className="font-semibold text-gray-900 flex items-center gap-2">
                            <span>{d.position_label ? `Поз. ${d.position_label}` : `Инстанс #${d.id}`}</span>
                            {d.qr_code && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                QR: {d.qr_code}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600 truncate">{d.spec_name}</div>
                          {d.description && (
                            <div className="text-[11px] text-gray-500 truncate">{d.description}</div>
                          )}
                        </div>
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" />}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )}

          {/* Optional fields */}
          {selectedInstanceId && (
            <div className="pt-2 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Серийный номер (S/N) устройства
                </label>
                <input
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder="например: SN-92841"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Примечание / Комментарий
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Монтаж на объекте"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Action button */}
          <div className="pt-4 flex items-center justify-end gap-3">
            <button
              type="submit"
              disabled={binding || !selectedInstanceId}
              className="w-full md:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium shadow-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {binding ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              <span>Привязать стикер и открыть устройство</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
